import { ActionTree } from 'vuex';

// Shared types
import {
    ApiMapResponse,
    Floor,
    RequestSpace,
    OutdoorUnitLocation,
    InsulationLevel,
} from '@coldtec/cos-types';

// Tool types
import { Nullable } from '@/types';

// Store and store types
import Store from '@/store';
import {
    RootState,
    SpacesMutation,
    SpacesAction,
    SystemsGetter,
    RequestAction,
    ToolSpace,
    ToolUnit,
    ToolAccessory,
} from '@/store/store.types';

// Api
import Api from '@/api';

// Validation
import Validation from '@/validation';

/**
 *
 *  Used to create a tool space, based on the
 *  forwared request space.
 *
 *  (i) They are valid by default, because they will
 *  be validated once submitted or they are loaded
 *  from the API
 *
 *  @param space The request space to construct the tool space for
 *
 */
function createToolSpace(space: Nullable<RequestSpace>): ToolSpace {

    return {
        data: space,
        errors: Validation.constructErrors<RequestSpace>(space),
        open: false,
        valid: true,
    };

}

/**
 *  Holding the actions of the spaces.
 */
export const Actions: ActionTree<RootState, RootState> = {

    /**
     *
     *  Used to fetch and store all needed data to configure a space.
     *
     *  (i) Only commits data to the store if all data was loaded succesfully
     *
     */
    async [SpacesAction.FETCH_CONFIG_DATA]({ commit }): Promise<void> {

        const floors = await Api.client.get<ApiMapResponse<Floor>>('/floors');
        const insulationLevels = await Api.client.get<ApiMapResponse<InsulationLevel>>('/insulation-levels');
        const outdoorUnitLocations = await Api.client.get<ApiMapResponse<OutdoorUnitLocation>>('/outdoor-unit-locations');

        commit(SpacesMutation.CONFIG_DATA, {
            floors: floors.data.map,
            insulation_levels: insulationLevels.data.map,
            outdoor_unit_locations: outdoorUnitLocations.data.map,
        });

        return Promise.resolve();

    },

    /**
     *  Used to sync the spaces of the request with the tool spaces.
     */
    [SpacesAction.SYNC_FROM_REQUEST]({ state, commit }): void {

        state.spaces.added = {};
        state.spaces.valid = true;

        /**
         *  Used to catch the first tool space id for
         *  programmatically opening it.
         */
        let toolSpaceId = '';

        for (const space of state.request.data.spaces) {

            /**
             *  Construct the tool space.
             */
            const toolSpace: ToolSpace = createToolSpace(space);

            /**
             *
             *  Ensure the length, width and height are reset to null
             *  because the validator of a space doesn't accept 0.
             *
             *  (i) If they are 0, a content is (/must have been) set
             *
             */
            for (const dimension of [ 'length', 'width', 'height' ] as Array<keyof RequestSpace>) {

                toolSpace.data[dimension] = toolSpace.data[dimension] === 0 ? null : toolSpace.data[dimension] as null;

            }

            /**
             *  Add the space.
             */
            commit(SpacesMutation.ADD, {
                [space.tool_space_id as string]: toolSpace,
            });

            /**
             *  Only catch the tool space id if it's not set already.
             */
            if (toolSpaceId.length) {

                continue;

            }

            toolSpaceId = space.tool_space_id;

        }

        /**
         *  Open the first space.
         */
        commit(SpacesMutation.TOGGLE, toolSpaceId);

    },

    /**
     *  Used to add a new space.
     */
    [SpacesAction.ADD]({ commit, state, dispatch }): ToolSpace {

        /**
         *  Create a local id to identify it in the tool.
         */
        const toolSpaceId: string = (new Date().getTime()).toString(36);

        /**
         *  Construct the nullable request space, because
         *  everything is yet to be filled in.
         */
        const space: Nullable<RequestSpace> = {
            tool_space_id: toolSpaceId,
            name: `Ruimte ${ Object.keys(state.spaces.added).length + 1 }`,

            // Content
            length: null,
            width: null,
            height: null,
            contents: null,

            // Settings
            floor: null,
            insulation_level: null,
            outdoor_unit_location: null,
            floor_description: null,
            with_outer_wall: 0,
            used_for_heating: 0,

            // Selected system, indoor unit, indoor unit color and accessories
            // (i) Filled in the systems step
            system: null,
            indoor_unit: null,
            color: null,
            accessories: [],
        };

        /**
         *
         *  Create the space tool wrapper, including
         *  the data and states.
         *
         *  (i) Valid by default, because that state is
         *  directly bound to the space's tile.
         *
         */
        const toolSpace: ToolSpace = createToolSpace(space);

        /**
         *  Add the new space.
         */
        commit(SpacesMutation.ADD, {
            [space.tool_space_id as string]: toolSpace,
        });

        /**
         *  Open the newly added space by default.
         */
        commit(SpacesMutation.TOGGLE, space.tool_space_id as string);

        /**
         *  Dispatch the action to sync the spaces of the request.
         */
        dispatch(RequestAction.SYNC_SPACES);

        /**
         *  Return the newly added space for further usage.
         */
        return toolSpace;

    },

    /**
     *  Used to reset the system selection of a space.
     */
    [SpacesAction.RESET_SYSTEM_SELECTION]({ commit, dispatch }, toolSpaceId: string): void {

        commit(SpacesAction.RESET_SYSTEM_SELECTION, toolSpaceId);

        /**
         *  Dispatch the action to sync the spaces of the request.
         */
        dispatch(RequestAction.SYNC_SPACES);

    },

    /**
     *
     *  Used to remove a space.
     *
     *  @param toolSpaceId The tool space id of the space to remove.
     *
     */
    [SpacesAction.REMOVE]({ commit, dispatch }, toolSpaceId: string): void {

        commit(SpacesMutation.REMOVE, toolSpaceId);

        /**
         *  Dispatch the action to sync the spaces of the request.
         */
        dispatch(RequestAction.SYNC_SPACES);

    },

    /**
     *
     *  Used to check if all spaces are valid.
     *
     *  (i) Validation of a single space happens in the `Spaces.vue` component.
     *
     */
    [SpacesAction.CHECK_VALIDITY]({ commit, state }): void {

        const spaces: Array<ToolSpace> = Object.values(state.spaces.added);

        let valid = spaces.length > 0;

        if (valid) {

            for (const space of Object.values(state.spaces.added)) {

                if (!space.valid) {

                    valid = false;

                    break;

                }

            }

        }

        commit(SpacesMutation.VALIDITY, valid);

    },

    /**
     *
     *  Used to select an indoor unit for a space.
     *
     *  (i) Also enables the invoker to reset a space's indoor unit by providing
     *  his local space id in the `resetLocalSpaceId`. This is needed because
     *  the `localSpaceId` will be `null` if it got reset, making it
     *  impossible to know which space should be resetted.
     *
     *  @param toolUnit The tool unit to select the space for, possibly including a local space id to reset
     *
     */
    [SpacesAction.SELECT_INDOOR_UNIT]({ state, dispatch }, toolUnit: ToolUnit): void {

        /**
         *  Set the provided unit's guid as indoor unit of the space.
         */
        if (toolUnit.toolSpaceId) {

            state.spaces.added[toolUnit.toolSpaceId].data.indoor_unit = toolUnit.guid;

        }

        /**
         *  Reset the space's indoor unit if requested.
         */
        if (
            toolUnit.resetToolSpaceId &&
            state.spaces.added[toolUnit.resetToolSpaceId].data.indoor_unit !== toolUnit.data.guid
        ) {

            state.spaces.added[toolUnit.resetToolSpaceId].data.indoor_unit = null;

        }

        /**
         *  Dispatch an action to sync the request spaces
         *  to preserve state on navigation.
         */
        dispatch(RequestAction.SYNC_SPACES);

    },

    /**
     *
     *  Used to select a color for the space's indoor unit.
     *
     *  @param toolUnit The tool unit which holds the local space id and the guid of the color to select
     *
     */
    [SpacesAction.SELECT_INDOOR_UNIT_COLOR]({ state, dispatch }, toolUnit: ToolUnit): void {

        const toolSpaceId: string | null = toolUnit.toolSpaceId || toolUnit.resetToolSpaceId;

        if (!toolSpaceId) {

            return;

        }

        state.spaces.added[toolSpaceId].data.color = toolUnit.color;

        /**
         *  Dispatch an action to sync the request spaces
         *  to preserve state on navigation.
         */
        dispatch(RequestAction.SYNC_SPACES);

    },

    /**
     *  Used to toggle a system accessory for the spaces for which the systems are currently shown.
     */
    [SpacesAction.TOGGLE_ACCESSORY]({ dispatch }, toolAccessory: ToolAccessory): void {

        /**
         *
         *  Always toggle the accessory of the first space for
         *  which the suggestions are shown. Which is per definition
         *  true for single splits, and a deliberate choice for
         *  multi split systems.
         *
         *  (i) There is always at least one space present,
         *  because this logic can't be reached without
         *
         */
        const space: ToolSpace = (Store.getters[SystemsGetter.SPACES] as Array<ToolSpace>)[0];

        if (!space.data.accessories) {

            return;

        }

        /**
         *  Get the index of the accessory's guid, check if its
         *  already added and add or remove him respectively.
         */
        const index: number = space.data.accessories.indexOf(toolAccessory.guid);
        const added = index > -1;

        if (!toolAccessory.selected && added) {

            space.data.accessories.splice(index, 1);

        } else if (toolAccessory.selected && !added) {

            space.data.accessories.push(toolAccessory.guid);

        }

        /**
         *  Dispatch an action to sync the request spaces
         *  to preserve state on navigation.
         */
        dispatch(RequestAction.SYNC_SPACES);

    },

};
